import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { planNameMessages } from '../../config/music24PlanNames';
import { SMLUpgradeModal } from '../SMLUpgradeModal/SMLUpgradeModal';
import { useUpgradeOffer } from '../SMLUpgradeModal/useUpgradeOffer';
import { upgradeFeatureIcons } from '../SMLUpgradeModal/upgradeFeatureIcons';
import { featureCopy, featureRowValue, upgradeMessages } from '../SMLUpgradeModal/upgradeMessages';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import styles from './SMLUpgradeLockBanner.module.scss';
import { withBrandWord } from '../primitives/BrandWord';

interface SMLUpgradeLockBannerProps {
  feature: SMLUpgradeFeature;
  /** What the person was looking at — an artist, a track, a playlist. */
  subject?: string;
  className?: string;
  /**
   * Where the button goes. A caller that already owns an upgrade modal passes its opener; without
   * one the banner carries its own.
   */
  onSeePlan?: () => void;
}

/**
 * The small variant, for a locked range or filter that sits inside a panel. The data the plan does
 * cover stays on screen and readable; this only says what the next plan adds, and what it costs.
 */
const SMLUpgradeLockBannerComponent: React.FC<SMLUpgradeLockBannerProps> = ({
  feature,
  subject,
  className,
  onSeePlan,
}) => {
  const intl = useIntl();
  const offer = useUpgradeOffer(feature);
  const isNativeEmbed = useIsNativeEmbed();
  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = useCallback(() => {
    if (onSeePlan !== undefined) {
      onSeePlan();
      return;
    }
    setModalOpen(true);
  }, [onSeePlan]);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const planName = intl.formatMessage(planNameMessages[offer.plan.tier]);

  const title = useMemo(() => {
    if (feature === SMLUpgradeFeature.history) {
      const extra = Math.max(offer.planCapabilities.historyDaysLimit - offer.currentCapabilities.historyDaysLimit, 0);
      return intl.formatMessage(upgradeMessages.bannerTitleDays, { count: extra });
    }
    if (feature === SMLUpgradeFeature.browseDepth) {
      const extra = Math.max(offer.planCapabilities.maxOffset - offer.currentCapabilities.maxOffset, 0);
      return intl.formatMessage(upgradeMessages.bannerTitleResults, { count: extra });
    }
    return intl.formatMessage(upgradeMessages.bannerTitleGeneric, { plan: planName });
  }, [intl, feature, planName, offer.planCapabilities, offer.currentCapabilities]);

  const description = intl.formatMessage(featureCopy[feature].valueDescription, {
    days: offer.planCapabilities.historyDaysLimit,
    count: offer.planCapabilities.maxOffset,
    artists: offer.planCapabilities.artistsFollowLimit,
    tracks: offer.planCapabilities.tracksFollowLimit,
    // The 13 free-plans features' descriptions quote the offered plan's own formatted number
    // instead (e.g. "60 a month of scheduled auto-posts."), via `featureRowValue`.
    targetText: featureRowValue(feature, offer.planCapabilities, intl),
  });
  // App Store 3.1.1 / Play billing: inside a native app's web view the note quotes no web price.
  const note = isNativeEmbed
    ? description
    : intl.formatMessage(upgradeMessages.bannerNote, { description, price: offer.monthlyPrice });

  return (
    <>
      <div className={clsx(styles.banner, className)}>
        <span className={styles.icon}>
          <FontAwesomeIcon icon={upgradeFeatureIcons[feature]} />
        </span>
        <span className={styles.text}>
          <span className={styles.title}>{title}</span>
          <span className={styles.note}>{withBrandWord(note)}</span>
        </span>
        <button type="button" className={styles.cta} onClick={openModal}>
          {intl.formatMessage(upgradeMessages.bannerCta, { plan: planName })}
        </button>
      </div>
      {onSeePlan === undefined && (
        <SMLUpgradeModal isOpen={isModalOpen} onClose={closeModal} feature={feature} subject={subject} />
      )}
    </>
  );
};

export const SMLUpgradeLockBanner = TypedMemo(SMLUpgradeLockBannerComponent);
